import React, { useEffect } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Linking } from 'react-native';
import { navigationRef } from '../services/navigationService';
import { linking } from './linking';

// Import category navigators
import AuthNavigator from './auth/AuthNavigator';
import DashboardNavigator from './dashboard/DashboardNavigator';
import ProfileNavigator from './profile/ProfileNavigator';
import DataNavigator from './data/DataNavigator';
import MedicalStackNavigator from './medical/MedicalStackNavigator';
import FinanceStackNavigator from './finance/FinanceStackNavigator';
import PersonalStackNavigator from './personal/PersonalStackNavigator';
import PropertyStackNavigator from './property/PropertyStackNavigator';
import FinalCareStackNavigator from './finalcare/FinalCareStackNavigator';
import HealthcareDirectivesStackNavigator from './healthcareDirectives/HealthcareDirectivesStackNavigator';
import EstatePlanningStackNavigator from './estatePlanning/EstatePlanningStackNavigator';
import CarePreferencesStackNavigator from './carePreferences/CarePreferencesStackNavigator';
import WishesStackNavigator from './wishes/WishesStackNavigator';
import DocumentsStackNavigator from './documents/DocumentsStackNavigator';
import ContactsStackNavigator from './contacts/ContactsStackNavigator';
import ExpensesStackNavigator from './expenses/ExpensesStackNavigator';
import LoyaltyStackNavigator from './loyalty/LoyaltyStackNavigator';
import PasswordsStackNavigator from './passwords/PasswordsStackNavigator';

// Import invitation screen
import AcceptInvitationScreen from '../screens/invitations/AcceptInvitationScreen';
import FamilyInviteScreen from '../screens/invitations/FamilyInviteScreen';
import { TrustedPartyWelcomeScreen } from '../screens/invitations/TrustedPartyWelcomeScreen';
import EmailVerificationResultScreen from '../screens/auth/EmailVerificationResultScreen';

// One-time vault migration screen (v1 → v2 encryption upgrade)
import MigrationScreen from '../screens/migration/MigrationScreen';

// New onboarding wizard (Phase 1) — gated by user.onboardingVersion in auth handlers
import { WizardNavigator } from '../screens/wizard/WizardNavigator';

// Letter-first onboarding (onboardingVersion 2) — new signups land here
import { LetterFirstNavigator } from '../screens/letterFirst/LetterFirstNavigator';

import { createLogger } from '../utils/logger';
import { analytics } from '../services/analytics';
const logger = createLogger('AppNavigator');

const Stack = createNativeStackNavigator();

// Deep linking configuration lives in ./linking (with a web sibling that
// maps every route to a URL so a browser refresh keeps its place).

const AppNavigator: React.FC = () => {
  useEffect(() => {
    // Debug: Log any incoming deep links
    const handleDeepLink = (event: { url: string }) => {
      logger.debug('🔗 Deep link received:', event.url);
    };

    // Listen for deep links while app is running
    const subscription = Linking.addEventListener('url', handleDeepLink);

    // Check if app was opened with a deep link
    Linking.getInitialURL().then((url) => {
      if (url) {
        logger.debug('🔗 Initial deep link URL:', url);
      }
    });

    return () => {
      subscription.remove();
    };
  }, []);

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      onReady={() => {
        const current = navigationRef.getCurrentRoute?.();
        if (current?.name) analytics.screen(current.name);
      }}
      onStateChange={(state) => {
        const routeName = state?.routes?.[state?.routes?.length - 1]?.name;
        logger.debug('📍 Navigation state changed:', routeName);
        const current = navigationRef.getCurrentRoute?.();
        if (current?.name) analytics.screen(current.name);
      }}
    >
      <Stack.Navigator
        screenOptions={{
          headerShown: false,
        }}
      >
        {/* Auth Flow */}
        <Stack.Screen 
          name="Auth" 
          component={AuthNavigator}
          options={{ gestureEnabled: false }}
        />
        
        {/* Dashboard */}
        <Stack.Screen name="Dashboard" component={DashboardNavigator} />

        {/* Onboarding Wizard (Phase 1 — new user flow) */}
        <Stack.Screen
          name="OnboardingWizard"
          component={WizardNavigator}
          options={{ gestureEnabled: false }}
        />

        {/* Letter-first onboarding (onboardingVersion 2 signups) */}
        <Stack.Screen
          name="LetterFirst"
          component={LetterFirstNavigator}
          options={{ gestureEnabled: false }}
        />
        
        {/* Profile & Settings */}
        <Stack.Screen name="Profile" component={ProfileNavigator} />
        
        {/* Data Management */}
        <Stack.Screen name="Data" component={DataNavigator} />
        
        {/* Task Categories */}
        <Stack.Screen name="Medical" component={MedicalStackNavigator} />
        <Stack.Screen name="Finance" component={FinanceStackNavigator} />
        <Stack.Screen name="Personal" component={PersonalStackNavigator} />
        <Stack.Screen name="Property" component={PropertyStackNavigator} />
        <Stack.Screen name="FinalCare" component={FinalCareStackNavigator} />
        <Stack.Screen name="HealthcareDirectives" component={HealthcareDirectivesStackNavigator} />
        <Stack.Screen name="EstatePlanning" component={EstatePlanningStackNavigator} />
        <Stack.Screen name="CarePreferences" component={CarePreferencesStackNavigator} />
        <Stack.Screen name="Wishes" component={WishesStackNavigator} />
        
        {/* Feature Categories */}
        <Stack.Screen name="Documents" component={DocumentsStackNavigator} />
        <Stack.Screen name="Contacts" component={ContactsStackNavigator} />
        <Stack.Screen name="Expenses" component={ExpensesStackNavigator} />
        <Stack.Screen name="Loyalty" component={LoyaltyStackNavigator} />
        <Stack.Screen name="Passwords" component={PasswordsStackNavigator} />
        
        {/* Invitation Flow */}
        <Stack.Screen 
          name="AcceptInvitation" 
          component={AcceptInvitationScreen}
          options={{ 
            gestureEnabled: false,
            headerShown: false 
          }}
        />
        
        {/* Family plan invitation link (myfinalkey.com/accept-invite/family) */}
        <Stack.Screen
          name="FamilyInvite"
          component={FamilyInviteScreen}
          options={{ gestureEnabled: false, headerShown: false }}
        />

        {/* Shown once after a successor or keyholder accepts */}
        <Stack.Screen
          name="TrustedPartyWelcome"
          component={TrustedPartyWelcomeScreen}
          options={{ gestureEnabled: false, headerShown: false }}
        />

        {/* Email Verification */}
        <Stack.Screen
          name="EmailVerificationResult"
          component={EmailVerificationResultScreen}
          options={{
            gestureEnabled: false,
            headerShown: false
          }}
        />

        {/* Vault migration — v1 to v2 encryption upgrade.
            Callers route here after auth when detectLegacyData reports items. */}
        <Stack.Screen
          name="VaultMigration"
          component={MigrationScreen}
          options={{
            gestureEnabled: false,
            headerShown: false,
          }}
        />
      </Stack.Navigator>
    </NavigationContainer>
  );
};

export default AppNavigator;